// TopicsList.js
import React, { useEffect, useState, useCallback } from 'react';
import { collection, getDocs, doc, deleteDoc, addDoc, updateDoc, query, where } from 'firebase/firestore';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { 
  Fab, 
  IconButton, 
  Dialog, 
  DialogActions, 
  DialogContent, 
  DialogTitle, 
  Button, 
  TextField, 
  Typography, 
  MenuItem, 
  Select, 
  FormControl, 
  InputLabel, 
  Alert, 
  Snackbar,
  Box,
  Paper,
  CircularProgress
} from "@mui/material";
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from "@mui/icons-material/Add";
import {db} from '../Configuration';
import { useFirebaseData } from '../contexts/FirebaseDataContext';
import OutlinedInput from '@mui/material/OutlinedInput';
import { v4 as uuidv4 } from 'uuid';
import { isUrduText, getUrduSxProps } from '../utils/urduFont';

const TopicsList = () => {
  const { campuses: contextCampuses, courses: contextCourses, initialLoadComplete } = useFirebaseData();
  const [topics, setTopics] = useState([]);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [topicName, setTopicName] = useState('');
  const [selectedCourse, setSelectedCourse] = useState('');
  const [selectedCampus, setSelectedCampus] = useState('');
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Filter courses that need predefined topics
  const coursesNeedingTopics = useCallback(() => {
    if (!contextCourses.length) return [];
    return contextCourses.filter(course => {
      if (!course.name) return false;
      const normalizedName = course.name.trim().toLowerCase();
      return normalizedName === 'special lectures' || 
             (normalizedName.includes('fikr') && normalizedName.includes('islami'));
    });
  }, [contextCourses]);

  const fetchTopics = useCallback(async () => {
    try {
      const topicsCollection = collection(db, 'Topics');
      const topicsSnapshot = await getDocs(topicsCollection);
      const topicsList = topicsSnapshot.docs.map((doc) => ({
        id: doc.id,
        topicId: doc.data().topicId || doc.id,
        ...doc.data(),
      }));
      
      // Filter by campus if selected
      if (selectedCampus) {
        // Get courses for selected campus
        const campusCourses = contextCourses.filter(course => 
          course.campusCodes && course.campusCodes.includes(selectedCampus)
        );
        const campusCourseNames = campusCourses.map(c => c.name);
        
        // Filter topics by course names
        const filtered = topicsList.filter(topic => 
          campusCourseNames.includes(topic.courseName)
        );
        setTopics(filtered);
      } else {
        setTopics(topicsList);
      }
    } catch (error) {
      console.error('Error fetching topics:', error);
      showSnackbar('Error loading topics. Please refresh the page.', 'error');
    }
  }, [selectedCampus, contextCourses]);

  const fetchFilteredCourses = useCallback(() => {
    if (!selectedCampus) {
      setFilteredCourses([]);
      return;
    }
    
    const courses = coursesNeedingTopics().filter(course => 
      course.campusCodes && course.campusCodes.includes(selectedCampus)
    );
    setFilteredCourses(courses);
  }, [selectedCampus, coursesNeedingTopics]);

  useEffect(() => {
    fetchFilteredCourses();
  }, [fetchFilteredCourses]);

  useEffect(() => {
    if (initialLoadComplete) {
      fetchTopics();
    }
  }, [fetchTopics, initialLoadComplete]);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleCampusChange = (event) => {
    setSelectedCampus(event.target.value);
  };

  const validateForm = () => {
    if (!topicName.trim()) {
      showSnackbar('Please enter a topic name', 'error');
      return false;
    }
    if (!selectedCourse) {
      showSnackbar('Please select a course', 'error');
      return false;
    }
    return true;
  };

  const handleAddTopic = async () => {
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      const topicId = uuidv4();
      const topicData = {
        topicId: topicId,
        name: topicName.trim(),
        courseName: selectedCourse,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      await addDoc(collection(db, 'Topics'), topicData);
      showSnackbar(`Topic "${topicName}" added successfully`, 'success');
      setOpenAddDialog(false);
      resetForm();
      fetchTopics();
    } catch (error) {
      console.error('Error adding topic:', error);
      showSnackbar('Error adding topic. Please try again.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditTopic = async () => {
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      const topicRef = doc(db, 'Topics', selectedTopic.id);
      const topicData = {
        name: topicName.trim(),
        courseName: selectedCourse,
        updatedAt: new Date()
      };

      await updateDoc(topicRef, topicData);
      showSnackbar(`Topic "${topicName}" updated successfully`, 'success');
      setOpenEditDialog(false);
      resetForm();
      fetchTopics();
    } catch (error) {
      console.error('Error updating topic:', error);
      showSnackbar('Error updating topic. Please try again.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteTopic = async (topicId) => {
    const topic = topics.find(t => t.id === topicId);
    if (window.confirm(`Are you sure you want to delete topic "${topic?.name}"? This action cannot be undone.`)) {
      try {
        await deleteDoc(doc(db, 'Topics', topicId));
        showSnackbar(`Topic "${topic?.name}" deleted successfully`, 'success');
        fetchTopics();
      } catch (error) {
        console.error('Error deleting topic:', error);
        showSnackbar('Error deleting topic. Please try again.', 'error');
      }
    }
  };

  const resetForm = () => {
    setTopicName('');
    setSelectedCourse('');
    setSelectedTopic(null);
  };

  const handleOpenEditDialog = (topic) => {
    setSelectedTopic(topic);
    setTopicName(topic.name);
    setSelectedCourse(topic.courseName);
    setOpenEditDialog(true);
  };

  // Auto-select campus if only one is available
  useEffect(() => {
    if (initialLoadComplete && contextCampuses.length === 1 && !selectedCampus) {
      setSelectedCampus(contextCampuses[0].campusCode);
    }
  }, [initialLoadComplete, contextCampuses, selectedCampus]);

  useEffect(() => {
    if (openAddDialog) {
      resetForm();
    }
  }, [openAddDialog]);

  useEffect(() => {
    if (openEditDialog && selectedTopic) {
      setTopicName(selectedTopic.name);
      setSelectedCourse(selectedTopic.courseName);
    }
  }, [openEditDialog, selectedTopic]);

  const columns = [
    { 
      field: 'name', 
      headerName: 'Topic Name', 
      width: 300,
      renderCell: (params) => {
        const value = params.value || '';
        const urdu = isUrduText(value);
        return (
          <Typography 
            className={urdu ? 'urdu' : ''} 
            variant="body2"
            sx={{
              ...getUrduSxProps(value),
              direction: 'ltr',
              textAlign: 'left'
            }}
          >
            {value}
          </Typography>
        );
      }
    },
    { field: 'courseName', headerName: 'Course', width: 200 },
    { field: 'topicId', headerName: 'Topic ID', width: 300 },
    {
      disableExport: true,
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      renderCell: (params) => (
        <>
          <IconButton
            color="primary"
            onClick={() => handleOpenEditDialog(params.row)}
            aria-label="edit"
            sx={{
              borderRadius: 2,
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              },
              transition: 'all 0.3s ease',
            }}
          >
            <EditIcon />
          </IconButton>
          <IconButton
            color="secondary"
            onClick={() => handleDeleteTopic(params.row.id)}
            aria-label="delete"
            sx={{
              borderRadius: 2,
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              },
              transition: 'all 0.3s ease',
            }}
          >
            <DeleteIcon />
          </IconButton>
        </>
      ),
    },
  ];

  return (
    <Box sx={{ p: 3, background: '#f8f9fa', minHeight: '100vh' }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Typography 
          variant="h4" 
          sx={{ 
            fontWeight: 700, 
            color: '#2E7D32',
            mb: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          📝 Topics Management
        </Typography>
        <Typography variant="body1" sx={{ color: '#666', mb: 2 }}>
          Manage predefined topics for Special Lectures and Fikr e Islami courses
        </Typography>
      </Box>

      {/* Campus Selection */}
      <Paper elevation={2} sx={{ mb: 3, p: 2, borderRadius: 2 }}>
        <FormControl sx={{ minWidth: 300, maxWidth: 400 }}>
          <InputLabel sx={{ color: '#4CAF50' }}>Select Campus</InputLabel>
          <Select
            value={selectedCampus}
            onChange={handleCampusChange}
            input={<OutlinedInput label="Select Campus" />}
            disabled={contextCampuses.length === 1}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '&:hover fieldset': {
                  borderColor: '#4CAF50',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#2E7D32',
                },
              },
            }}
          >
            {contextCampuses.map((campus) => (
              <MenuItem key={campus.id} value={campus.campusCode}>
                {campus.campusName} ({campus.campusCode})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Paper>

      {!selectedCampus && (
        <Paper sx={{ p: 4, textAlign: 'center', mb: 3 }}>
          <Typography variant="h6" color="text.secondary">
            Please select a campus to view topics
          </Typography>
        </Paper>
      )}

      {/* Topics Data Grid Card */}
      {selectedCampus && (
      <Paper 
        elevation={2} 
        sx={{ 
          p: 3, 
          mb: 3, 
          borderRadius: 3,
          background: 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)',
          border: '1px solid rgba(76, 175, 80, 0.1)'
        }}
      >
        <Typography 
          variant="h6" 
          sx={{ 
            fontWeight: 600, 
            color: '#2E7D32',
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          📊 Topics Records
        </Typography>
        
        <DataGrid
          rows={topics}
          columns={columns}
          slots={{ toolbar: GridToolbar }}
          sx={{
            border: 'none',
            height: 600,
            '& .MuiDataGrid-root': {
              border: 'none',
            },
            '& .MuiDataGrid-cell': {
              borderBottom: '1px solid rgba(76, 175, 80, 0.1)',
              display: 'flex',
              alignItems: 'center',
            },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#f8f9fa',
              borderBottom: '2px solid #4CAF50',
            },
          }}
        />
      </Paper>
      )}

      {/* Floating Action Button */}
      <Box sx={{ 
        position: 'fixed', 
        bottom: 20, 
        right: 20, 
        zIndex: 1000
      }}>
        <Fab 
          color="primary" 
          aria-label="add" 
          onClick={() => setOpenAddDialog(true)}
          disabled={!selectedCampus || filteredCourses.length === 0}
          sx={{
            backgroundColor: '#4CAF50',
            color: '#fff',
            '&:hover': {
              backgroundColor: '#2E7D32',
              transform: 'translateY(-2px)',
              boxShadow: '0 6px 20px rgba(76, 175, 80, 0.3)',
            },
            '&.Mui-disabled': {
              backgroundColor: '#ccc',
            },
            transition: 'all 0.3s ease',
          }}
        >
          <AddIcon />
        </Fab>
      </Box>

      {/* Add Topic Dialog */}
      <Dialog open={openAddDialog} onClose={() => setOpenAddDialog(false)}>
        <DialogTitle>Add New Topic</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="dense">
            <InputLabel>Course</InputLabel>
            <Select
              value={selectedCourse}
              onChange={(e) => setSelectedCourse(e.target.value)}
              input={<OutlinedInput label="Course" />}
            >
              {filteredCourses.map((course) => (
                <MenuItem key={course.id} value={course.name}>
                  {course.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            autoFocus
            margin="dense"
            label="Topic Name"
            type="text"
            fullWidth
            value={topicName}
            onChange={(e) => setTopicName(e.target.value)}
            sx={{ 
              mt: 2,
              '& .MuiInputBase-input': {
                fontFamily: isUrduText(topicName)
                  ? "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', serif"
                  : 'inherit',
                direction: 'ltr',
                textAlign: 'left'
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddDialog(false)} color="primary" disabled={submitting}>
            Cancel
          </Button>
          <Button 
            onClick={handleAddTopic} 
            color="primary" 
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={20} /> : null}
          >
            {submitting ? 'Adding...' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Topic Dialog */}
      <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)}>
        <DialogTitle>Edit Topic</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="dense">
            <InputLabel>Course</InputLabel>
            <Select
              value={selectedCourse}
              onChange={(e) => setSelectedCourse(e.target.value)}
              input={<OutlinedInput label="Course" />}
            >
              {filteredCourses.map((course) => (
                <MenuItem key={course.id} value={course.name}>
                  {course.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            autoFocus
            margin="dense"
            label="Topic Name"
            type="text"
            fullWidth
            value={topicName}
            onChange={(e) => setTopicName(e.target.value)}
            sx={{ 
              mt: 2,
              '& .MuiInputBase-input': {
                fontFamily: isUrduText(topicName)
                  ? "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', serif"
                  : 'inherit',
                direction: 'ltr',
                textAlign: 'left'
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditDialog(false)} color="primary" disabled={submitting}>
            Cancel
          </Button>
          <Button 
            onClick={handleEditTopic} 
            color="primary" 
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={20} /> : null}
          >
            {submitting ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{ zIndex: 99999 }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TopicsList;



